import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';

import { useUiContext } from '../../../../contexts/UiContext/hooks/useUiContext.hook';
import { useDialogAutoFocus } from '../../../../hooks/useDialogAutoFocus.hook';

import { MemoBeforeSubmitForm } from '../../../MemoBeforeSubmit';
import { useMemos } from '../../../MemoBeforeSubmit/hooks/useMemos.hook';
import { AlertMessages } from '../../../../components/AlertMessages';

import type { SubmitHandler } from './types/submit-handler.type';

// Specify copies the list inherited as it is (roles included); inherit drops the list of its own.
export enum ListInheritanceChange {
  Customize = 'customize',
  Flush = 'flush',
}

const TEXTS: Readonly<
  Record<
    ListInheritanceChange,
    Readonly<{ submitLabel: string; title: string; question: string }>
  >
> = {
  [ListInheritanceChange.Customize]: {
    submitLabel: 'Spécifier',
    title: 'Confirmation de la copie de la liste héritée',
    question:
      'Êtes-vous sûr de vouloir spécifier cette liste d’acteurs ? Elle est copiée telle quelle, rôles compris, puis ne suit plus le niveau parent.',
  },
  [ListInheritanceChange.Flush]: {
    submitLabel: 'Hériter',
    title: 'Confirmation du retour à la liste héritée',
    question:
      'Êtes-vous sûr de vouloir hériter de cette liste d’acteurs ? Ses acteurs propres sont remplacés par ceux du niveau parent.',
  },
};

interface Props<V extends object> {
  readonly change: ListInheritanceChange;
  readonly askMemoBeforeSubmit?: boolean;
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<V>;
  readonly useDirectMutation: boolean;
}

export const ListInheritanceForm = <V extends object>({
  change,
  askMemoBeforeSubmit = true,
  isDialogOpen,
  onCloseDialog: handleCloseDialog,
  onSubmit: pHandleSubmit,
  useDirectMutation,
}: Readonly<Props<V>>) => {
  const { ui } = useUiContext();

  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  // on error
  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { clearMemos, memos, setMemos } = useMemos();

  const handleSubmit = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      void pHandleSubmit({ values, memos, formikHelpers, setFormError })?.then(
        clearMemos,
      );
    },
    [clearMemos, pHandleSubmit, memos],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={EMPTY_OBJECT as V}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: formikSubmit, isSubmitting, submitForm, isValid }) => {
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        const { submitLabel, title, question } = TEXTS[change];

        return (
          <Form onSubmit={formikSubmit}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!askMemoBeforeSubmit && (
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
            )}
            <Dialog
              aria-describedby={`${change}-actors-dialog-description`}
              aria-labelledby={`${change}-actors-dialog-title`}
              onClose={handleCloseDialog}
              open={isDialogOpen}
              slotProps={dialogAutoFocusSlotProps}
            >
              <DialogTitle id={`${change}-actors-dialog-title`}>
                {title}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id={`${change}-actors-dialog-description`}>
                  {question}
                  {useDirectMutation
                    ? ' Cette action est définitive.'
                    : EMPTY_STRING}
                </DialogContentText>
                {!!formError && (
                  <AlertMessages messages={formError} severity="error" />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} size="large">
                  Annuler
                </Button>
                <Button
                  color="warning"
                  data-dialog-autofocus
                  disabled={!isValid}
                  loading={isLoading}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={
                    askMemoBeforeSubmit
                      ? () => {
                          setMemos((oldMemos) => ({
                            ...oldMemos,
                            isOpenMemoDialog: true,
                          }));
                        }
                      : submitForm
                  }
                  size="large"
                  variant="contained"
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};
